import { getRequest } from '../Util/utils';
import { postComposerPayload, simpleResponse } from '../Messaging/composerRequest';
import type { ExerciseReference, MessageAttachment, MessagingUser } from '../Messaging/api';

// A public discussion thread on a specific activity or calendar day. Unlike a Messaging
// Conversation there's no participants list -- anyone logged in as the subject athlete (the
// athlete themself, or any of their coaches, whether viewing directly or "logged in as" them) can
// read and post. Never auto-created: posting the first comment is what brings it into existence.
export type CommentThread = {
  id: string;
  type: 'activity' | 'day';
  last_comment_at?: string | null;
  last_comment_preview?: string | null;
  // Everyone with access to this thread (the subject athlete + their coaches) -- unlike
  // Conversation.participants this isn't used for authorization (that's still server-side),
  // just so the client has avatar data to render read receipts against.
  participants: MessagingUser[];
};

export type CommentReplyPreview = { id: string; body: string | null; author: MessagingUser | null; deleted: boolean };

export type Comment = {
  id: string;
  thread_id: string;
  body: string | null;
  attachments: MessageAttachment[];
  created_at: string;
  // Who actually wrote this -- the athlete, or the specific coach who was logged in as them at
  // the time. Comments are public, so every comment carries its own author rather than relying on
  // a small fixed participants list the client would otherwise need to cross-reference.
  author: MessagingUser | null;
  read_by: { user_id: number; at: string }[];
  edited_at?: string | null;
  deleted_at?: string | null;
  reactions: { user_id: number; emoji: string }[];
  pinned_at?: string | null;
  reply_to: CommentReplyPreview | null;
  forwarded_from: MessagingUser | null;
  // True if this comment was ever relayed to/from WhatsApp -- see ChatMessage.via_whatsapp
  // (Messaging/api.ts) for the shared server-side logic (BaseMessageService::viaWhatsApp()).
  via_whatsapp: boolean;
};

export type CommentThreadData = {
  thread: CommentThread | null;
  comments: Comment[];
};

export type ForwardTarget = { id: string; label: string };
export type ForwardTargets = { conversations: ForwardTarget[]; threads: ForwardTarget[] };

// One row of getMyThreads() -- every thread this user has access to (their own, plus every
// athlete they coach's), for the centralized Messages page's merged inbox. Distinct from
// CommentThread above: this carries the identifying activity_path/day (so the inbox can build the
// load/send/markRead closures CommentThreadPanel needs) and the subject/unread_count a single
// thread view has no reason to know about itself.
// A discriminated union on `type` -- narrowing on `type === 'activity'`/`'day'` (rather than
// declaring both fields `string | null` unconditionally) lets TypeScript know activity_path/day is
// non-null in the branch that actually uses it, instead of every caller needing its own assertion.
export type MyCommentThread = {
  id: string;
  // Activity name, or a formatted day string -- resolved server-side (CommentService::threadLabel())
  // since the frontend only has the raw activity_path/day to go on otherwise.
  title: string;
  subject: MessagingUser | null;
  last_comment_at: string | null;
  last_comment_preview: string | null;
  unread_count: number;
} & (
  | { type: 'activity'; activity_path: string; day: null }
  | { type: 'day'; activity_path: null; day: string }
);

export async function getMyThreads(): Promise<MyCommentThread[]> {
  return window
    .fetch('/comments/threads', getRequest())
    .then((r) => simpleResponse<{ threads: MyCommentThread[] }>(r))
    .then((data) => data.threads);
}

// Batch {total, unread} counts for a list of activity paths, for list/dashboard badges that
// would otherwise need one request per card (calendar, Today, /activities, coach dashboard).
export async function getActivityCommentCounts(paths: string[]): Promise<Record<string, { total: number; unread: number }>> {
  return window
    .fetch('/comments/activities/counts', { ...getRequest(), method: 'POST', body: JSON.stringify({ paths }) })
    .then((r) => simpleResponse<Record<string, { total: number; unread: number }>>(r));
}

export async function getActivityComments(activityPath: string, before?: string): Promise<CommentThreadData> {
  const params = before ? '?' + new URLSearchParams({ before }) : '';
  return window.fetch(`/comments/activities/${activityPath}${params}`, getRequest()).then((r) => simpleResponse<CommentThreadData>(r));
}

// Posts body text plus any staged files/exercise references as a single comment. See
// composerRequest.ts::postComposerPayload for the JSON-vs-multipart transport logic.
export async function postActivityComment(activityPath: string, body: string, files: File[] = [], references: ExerciseReference[] = [], replyToId?: string): Promise<{ thread: CommentThread; comment: Comment }> {
  return postComposerPayload<{ thread: CommentThread; comment: Comment }>(`/comments/activities/${activityPath}`, { body, files, references, replyToId });
}

// $day must be a 'YYYY-MM-DD' string produced by LocalDate.format() -- the same opaque key already
// used for calendar notes, so day identity always matches between the two features.
// $forUser is the athlete's username, only needed when a coach is viewing that athlete's calendar
// (mirrors the forUser convention CalendarController's notes endpoints already use) -- omitted when
// the athlete is viewing their own calendar.
export async function markActivityCommentsRead(activityPath: string): Promise<void> {
  await window.fetch(`/comments/activities/${activityPath}/read`, { ...getRequest(), method: 'POST' });
}

export async function markDayCommentsRead(day: string, forUser?: string): Promise<void> {
  const query = forUser ? '?' + new URLSearchParams({ forUser }) : '';
  await window.fetch(`/comments/days/${day}/read${query}`, { ...getRequest(), method: 'POST' });
}

// Batch {total, unread} counts for a list of 'YYYY-MM-DD' days, for the calendar's per-day comment
// badges -- the day-thread twin of getActivityCommentCounts. Days with no thread are omitted from
// the response. $forUser follows the same convention as getDayComments below.
export async function getDayCommentCounts(days: string[], forUser?: string): Promise<Record<string, { total: number; unread: number }>> {
  return window
    .fetch('/comments/days/counts', { ...getRequest(), method: 'POST', body: JSON.stringify({ days, forUser }) })
    .then((r) => simpleResponse<Record<string, { total: number; unread: number }>>(r));
}

export async function getDayComments(day: string, forUser?: string, before?: string): Promise<CommentThreadData> {
  const query: Record<string, string> = {};
  if (forUser) query.forUser = forUser;
  if (before) query.before = before;
  const params = Object.keys(query).length ? '?' + new URLSearchParams(query) : '';
  return window.fetch(`/comments/days/${day}${params}`, getRequest()).then((r) => simpleResponse<CommentThreadData>(r));
}

export async function postDayComment(day: string, body: string, forUser?: string, files: File[] = [], references: ExerciseReference[] = [], replyToId?: string): Promise<{ thread: CommentThread; comment: Comment }> {
  return postComposerPayload<{ thread: CommentThread; comment: Comment }>(`/comments/days/${day}`, { body, files, references, replyToId, extra: { forUser } });
}

export async function editComment(commentId: string, body: string): Promise<Comment> {
  return window
    .fetch(`/comments/messages/${commentId}`, { ...getRequest(), method: 'PUT', body: JSON.stringify({ body }) })
    .then((r) => simpleResponse<{ comment: Comment }>(r))
    .then((data) => data.comment);
}

export async function deleteComment(commentId: string): Promise<void> {
  await window.fetch(`/comments/messages/${commentId}`, { ...getRequest(), method: 'DELETE' });
}

export async function toggleCommentReaction(commentId: string, emoji: string): Promise<Comment> {
  return window
    .fetch(`/comments/messages/${commentId}/reactions`, { ...getRequest(), method: 'POST', body: JSON.stringify({ emoji }) })
    .then((r) => simpleResponse<{ comment: Comment }>(r))
    .then((data) => data.comment);
}

export async function pinComment(commentId: string): Promise<Comment> {
  return window
    .fetch(`/comments/messages/${commentId}/pin`, { ...getRequest(), method: 'POST' })
    .then((r) => simpleResponse<{ comment: Comment }>(r))
    .then((data) => data.comment);
}

export async function unpinComment(commentId: string): Promise<Comment> {
  return window
    .fetch(`/comments/messages/${commentId}/pin`, { ...getRequest(), method: 'DELETE' })
    .then((r) => simpleResponse<{ comment: Comment }>(r))
    .then((data) => data.comment);
}

export async function getForwardTargets(threadId: string): Promise<ForwardTargets> {
  return window.fetch(`/comments/threads/${threadId}/forward-targets`, getRequest()).then((r) => simpleResponse<ForwardTargets>(r));
}

// Forwarding a comment into a conversation turns it into a plain ChatMessage (imported from
// Messaging/api since that's genuinely what the backend returns for that path); forwarding into
// another comment thread returns a Comment, same shape as posting one.
export async function forwardCommentToConversation(commentId: string, conversationId: string): Promise<import('../Messaging/api').ChatMessage> {
  return window
    .fetch(`/comments/messages/${commentId}/forward`, {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify({ target_type: 'conversation', target_id: conversationId }),
    })
    .then((r) => simpleResponse<{ message: import('../Messaging/api').ChatMessage }>(r))
    .then((data) => data.message);
}

export async function forwardCommentToThread(commentId: string, targetThreadId: string): Promise<Comment> {
  return window
    .fetch(`/comments/messages/${commentId}/forward`, {
      ...getRequest(),
      method: 'POST',
      body: JSON.stringify({ target_type: 'thread', target_id: targetThreadId }),
    })
    .then((r) => simpleResponse<{ comment: Comment }>(r))
    .then((data) => data.comment);
}
